export const getVideoDuration = (file, setValue, name) => {
  var video = document.createElement('video')
  video.preload = 'metadata'
  video.onloadedmetadata = function () {
    window.URL.revokeObjectURL(video.src)
    setValue(name, getTimeDurationString(video.duration))
  }
  video.src = URL.createObjectURL(file)
  video.remove()
}

export const getTimeDurationString = (ms) => {
  let hrs = Math.floor(ms / 1000 / 60 / 60)
  let mins = Math.floor(ms / 1000 / 60) % 60
  let secs = (ms / 1000) % 60

  if (hrs < 1) {
    hrs = '00'
  } else {
    hrs = hrs.toString().padStart(2, '0')
  }

  if (mins < 1) {
    mins = '00'
  } else {
    mins = mins.toString().padStart(2, '0')
  }

  return `${hrs}:${mins}:${secs.toFixed(2).toString().padStart(5, '0')}`
}

export const getTimeWithoutFractional = (timestring) => timestring.split('.')[0]

export const parseDurationString = (str) => {
  if (!str) return 0

  if (typeof str === 'number') {
    return str
  }

  // Accept formats like "00:01:00.00" or "1:30"
  const parts = str.split(':').map(Number)

  if (parts.length === 3) {
    const [h, m, s] = parts
    return h * 3600 + m * 60 + s
  }
  if (parts.length === 2) {
    const [m, s] = parts
    return m * 60 + s
  }

  return Number(str) || 0
}

export const normalizeDurationString = (value) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return '00:00:00.00'
  }

  const seconds = parseDurationString(value)
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '00:00:00.00'
  }

  return getTimeDurationString(seconds * 1000)
}

/**
 * A duration as whole minutes for display: "00:33:49.92" -> "34min".
 *
 * Rounds to the nearest minute rather than dropping the leftover seconds. The mobile app formats
 * the same stored duration for the same reader, so a video that runs 33:49 has to read as 34
 * minutes in both places or a coach sees one length while their client sees another.
 *
 * Mobile's rule is "more than 30 leftover seconds rounds up", which leaves an exact half minute
 * where it is. That is matched deliberately instead of using Math.round, which rounds an exact
 * half up and would put the two apps a minute apart on any duration landing on :30 — reachable
 * for the exercise-based estimates, which are built by adding up whole seconds.
 */
export const getReadableTime = (timeStr) => {
  const seconds = parseDurationString(timeStr)

  // Whole seconds first, so fractional seconds cannot tip the comparison below on their own.
  const wholeSeconds = Math.floor(seconds)
  const leftoverSeconds = wholeSeconds % 60
  const roundedMinutes = Math.floor(wholeSeconds / 60) + (leftoverSeconds > 30 ? 1 : 0)

  const totalMinutes = Math.max(roundedMinutes, 1)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  let result = ''
  if (hours > 0) result += `${hours}h`
  if (hours > 0 && minutes > 0) result += ' '
  if (minutes > 0) result += `${minutes}min`

  return result
}

/**
 * The whole-second spelling of a stored duration: "00:00:03.57" -> "00:00:04.00".
 *
 * Some stored durations carry fractional seconds — a backfill script wrote library per-rep
 * estimates that way — while everything the dashboard writes or compares is whole seconds
 * (the Time input cannot even hold a fraction). Rounding where a stored value is read keeps
 * one figure to one spelling; otherwise string comparisons against an input's value report
 * edits the coach never made.
 *
 * A missing value (undefined, null, "") passes through untouched, so "is there a value at
 * all?" checks on the result still work.
 */
export const roundDurationString = (value) => {
  if (value === undefined || value === null || value === '') {
    return value
  }

  const seconds = parseDurationString(value)
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '00:00:00.00'
  }

  return getTimeDurationString(Math.round(seconds) * 1000)
}

export const getReadableBreakdownTime = (timeStr) => {
  // Stored durations can carry fractional seconds; a coach never reads those.
  const seconds = Math.round(parseDurationString(timeStr))
  if (seconds <= 0) return '0s'

  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  const parts = []
  if (hours > 0) parts.push(`${hours}h`)
  if (mins > 0) parts.push(`${mins}min`)
  if (secs > 0) parts.push(`${secs}s`)
  return parts.join(' ') || '0s'
}
